import React from "react";
import { Handshake, Star, Building, Check, X, ArrowRight } from "lucide-react";
import { LegalLayout, LegalSection, CheckList } from "./legal/LegalLayout";

const partnershipBenefits = [
  "Dedicated Virtual Guard exclusively assigned to your community",
  "Reduced insurance premiums through enhanced security documentation",
  "Comprehensive security reporting for board meetings and residents",
  "Professional camera installation with no upfront community costs",
  "License plate reader systems for visitor and resident tracking",
  "24/7 remote monitoring from our Texas-based command center",
  "Direct coordination with local law enforcement when needed",
  "Cluster mailbox protection solutions and gate access management integration",
];

const communityTypes = [
  { name: "Condominium Complexes", description: "High-rise and low-rise residential communities" },
  { name: "Gated Residential", description: "Single-family home communities with controlled access" },
  { name: "Townhome Communities", description: "Multi-unit residential developments" },
  { name: "Mixed-Use Developments", description: "Residential and commercial property combinations" },
  { name: "Master-Planned Communities", description: "Large-scale residential developments with amenities" },
];

const steps = [
  { n: "1", title: "Private Assessment", body: "A virtual security consultation for your community. We assess your property layout, current security gaps, and specific HOA needs." },
  { n: "2", title: "Custom Installation", body: "Professional camera installation, license plate readers, and gate access integration. No upfront costs for equipment or installation." },
  { n: "3", title: "Dedicated Service", body: "Your assigned Virtual Guard begins 24/7 monitoring, learns your community patterns, and provides consistent professional coverage." },
];

const traditional = [
  "Random patrol schedules (criminals learn patterns)",
  "Limited coverage hours and property visibility",
  "Queue-based response (multiple properties per guard)",
  "No familiarity with residents or daily patterns",
  "Expensive on-site personnel costs",
];

const redline = [
  "Dedicated guard exclusively assigned to your community",
  "24/7 continuous monitoring and complete property coverage",
  "Proactive threat detection and immediate response",
  "Learns residents, patterns, and community rhythms",
  "Cost-effective with professional documentation",
];

const HOAPartnership = () => (
  <LegalLayout
    eyebrow="Community Partnership"
    eyebrowIcon={Handshake}
    title="HOA Partnership Program"
    subtitle="Transform your HOA community security with dedicated Virtual Guards. Professional remote monitoring designed for condominiums, multi-family communities, and gated properties."
    maxWidth="max-w-6xl"
  >
    <div className="space-y-12">
      <LegalSection icon={Star} title="Partnership Benefits">
        <p>Redline's dedicated Virtual Guard model gives your HOA community exclusive coverage that learns your residents, understands your property patterns, and delivers proactive protection.</p>
        <CheckList color="green" cols={2} items={partnershipBenefits} />
      </LegalSection>

      {/* Community Types */}
      <div>
        <h2 className="font-head text-3xl font-bold text-[#1A1A1A] mb-3">Community Types We Serve</h2>
        <p className="text-[#1A1A1A]/65 mb-8 max-w-3xl">Our Virtual Guard services are designed for the full range of HOA communities and residential properties.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A1A1A]/10 border border-[#1A1A1A]/10">
          {communityTypes.map((c) => (
            <div key={c.name} className="bg-white p-8 group hover:bg-[#1A1A1A] transition-colors duration-300">
              <span className="w-11 h-11 bg-[#D32F2F]/10 flex items-center justify-center mb-5 group-hover:bg-[#D32F2F] transition-colors">
                <Building className="w-5 h-5 text-[#D32F2F] group-hover:text-white transition-colors" />
              </span>
              <h3 className="font-head text-lg font-bold text-[#1A1A1A] mb-2 group-hover:text-white transition-colors">{c.name}</h3>
              <p className="text-sm text-[#1A1A1A]/60 group-hover:text-white/70 transition-colors">{c.description}</p>
            </div>
          ))}
        </div>
      </div>

      <LegalSection title="How Our Partnership Works">
        <div className="grid lg:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div key={s.n}>
              <div className="w-12 h-12 bg-[#D32F2F] text-white font-head text-xl font-extrabold flex items-center justify-center mb-5">{s.n}</div>
              <h3 className="font-head text-xl font-bold text-[#1A1A1A] mb-3">{s.title}</h3>
              <p className="text-[#1A1A1A]/65 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </LegalSection>

      {/* Comparison */}
      <div>
        <h2 className="font-head text-3xl font-bold text-[#1A1A1A] mb-8">Why Virtual Guards vs. Traditional Security?</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-[#EFEFEC] border border-[#1A1A1A]/10 p-8">
            <h3 className="font-head text-lg font-bold text-[#1A1A1A]/60 mb-6">Traditional Patrol Security</h3>
            <ul className="space-y-3">
              {traditional.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[#1A1A1A]/55">
                  <X className="w-4 h-4 text-[#1A1A1A]/30 shrink-0 mt-0.5" /><span className="text-sm">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#1A1A1A] p-8">
            <div className="h-1 bg-[#D32F2F] -mt-8 -mx-8 mb-6" />
            <h3 className="font-head text-lg font-bold text-white mb-6">Redline Virtual Guards</h3>
            <ul className="space-y-3">
              {redline.map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-white/90">
                  <span className="w-5 h-5 bg-[#D32F2F] flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[#F7F7F5] border border-[#1A1A1A]/10 p-10 text-center">
        <h2 className="font-head text-3xl font-bold text-[#1A1A1A] mb-4">Ready to Partner with Redline?</h2>
        <p className="text-[#1A1A1A]/65 max-w-2xl mx-auto mb-10">Join HOA communities across Texas who have upgraded their security with dedicated Virtual Guards — professional, reliable, premium community protection.</p>
        <div className="grid sm:grid-cols-3 gap-8 max-w-2xl mx-auto mb-10">
          {[["24/7", "Continuous Monitoring"], ["1:1", "Dedicated Guard Ratio"], ["TX", "DPS Licensed #B30959201"]].map(([n, l]) => (
            <div key={l}>
              <div className="font-head text-4xl font-extrabold text-[#D32F2F] mb-1">{n}</div>
              <div className="text-sm text-[#1A1A1A]/60">{l}</div>
            </div>
          ))}
        </div>
        <button
          onClick={() => { window.location.href = '/#consultation'; }}
          data-testid="hoa-cta"
          className="group inline-flex items-center gap-3 bg-[#D32F2F] hover:bg-[#1A1A1A] text-white text-sm font-semibold uppercase tracking-wide px-8 py-4 transition-colors"
        >
          Schedule a Partnership Consultation
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
        <p className="text-[#1A1A1A]/45 text-xs mt-5">Private consultation · No obligation · Custom security assessment for your community</p>
      </div>
    </div>
  </LegalLayout>
);

export default HOAPartnership;
